import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CommandPalette, type CommandItem } from '@teivor/shared-ui';

import { navItemsForRole } from '../lib/nav';
import { useSession } from '../lib/session';

/**
 * Lightweight ⌘K / Ctrl-K palette for the client portal — navigation only
 * (no record search). cmdk owns the keyboard shortcut. Destinations are
 * role-scoped so a rep never jumps to a client-only page (and vice versa).
 */
export function GlobalCommandPalette() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const navItems = navItemsForRole(session?.role);

  const commands = React.useMemo<CommandItem[]>(
    () =>
      navItems.map((item) => {
        const Icon = item.glyph.line;
        return {
          id: `nav:${item.to}`,
          label: t(item.labelKey),
          keywords: [item.to],
          group: t('cmdk.group.navigate', 'Navigate'),
          icon: <Icon size={16} color="currentColor" aria-hidden />,
          run: () => navigate(item.to),
        };
      }),
    [t, navigate, navItems],
  );

  return (
    <CommandPalette
      commands={commands}
      placeholder={t('cmdk.placeholder', 'Jump to a page…')}
      emptyMessage={t('cmdk.empty', 'No results.')}
      recentsKey="teivor.client.cmdk.recents"
    />
  );
}
